import { arrayMove } from '@dnd-kit/sortable';

const EMPTY_GROUP_DROP_PREFIX = 'empty-group-drop:';

/** Droppable id for an empty group bucket (lets users drop items into a group with no rows). */
export function emptyGroupDropId(groupKey: string): string {
  return `${EMPTY_GROUP_DROP_PREFIX}${groupKey}`;
}

export function parseEmptyGroupDropId(overId: string): string | null {
  if (!overId.startsWith(EMPTY_GROUP_DROP_PREFIX)) {
    return null;
  }
  return overId.slice(EMPTY_GROUP_DROP_PREFIX.length);
}

/** Map from `groupLabelToKey` back to the group label stored on `LayoutSpec.group` (undefined = ungrouped). */
export function groupLabelForGroupKey(key: string): string | undefined {
  return key === '__ungrouped__' ? undefined : key;
}

/** Stable map key for `group.label` (undefined = ungrouped bucket). */
export function groupLabelToKey(label: string | undefined): string {
  return label ?? '__ungrouped__';
}

export function findGroupKeyForId(orderByGroup: Map<string, string[]>, id: string): string | undefined {
  for (const [key, ids] of orderByGroup) {
    if (ids.includes(id)) {
      return key;
    }
  }
  return undefined;
}

/**
 * Reorders `orderByGroup` / `groups` for a drag of `activeId` relative to `overId`.
 * Used from onDragOver (live) and onDragEnd (final sync). Returns null if nothing changes.
 */
export function applyReorder(
  orderByGroup: Map<string, string[]>,
  groups: Map<string, string | undefined>,
  activeId: string,
  overId: string
): { orderByGroup: Map<string, string[]>; groups: Map<string, string | undefined> } | null {
  if (activeId === overId) {
    return null;
  }

  const emptyDropKey = parseEmptyGroupDropId(overId);
  if (emptyDropKey != null) {
    const next = new Map(orderByGroup);
    const activeKey = findGroupKeyForId(next, activeId);
    if (activeKey == null) {
      return null;
    }
    if (activeKey === emptyDropKey) {
      return null;
    }
    const sourceList = [...(next.get(activeKey) ?? [])];
    const from = sourceList.indexOf(activeId);
    if (from === -1) {
      return null;
    }
    sourceList.splice(from, 1);
    next.set(activeKey, sourceList);
    const targetList = [...(next.get(emptyDropKey) ?? []), activeId];
    next.set(emptyDropKey, targetList);

    const nextGroups = new Map(groups);
    nextGroups.set(activeId, groupLabelForGroupKey(emptyDropKey));

    return { orderByGroup: next, groups: nextGroups };
  }

  const next = new Map(orderByGroup);
  const activeKey = findGroupKeyForId(next, activeId);
  const overKey = findGroupKeyForId(next, overId);
  if (activeKey == null || overKey == null) {
    return null;
  }

  if (activeKey === overKey) {
    const list = [...(next.get(activeKey) ?? [])];
    const oldIndex = list.indexOf(activeId);
    const newIndex = list.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      return null;
    }
    next.set(activeKey, arrayMove(list, oldIndex, newIndex));
    return { orderByGroup: next, groups };
  }

  const sourceList = [...(next.get(activeKey) ?? [])];
  const targetList = [...(next.get(overKey) ?? [])];
  const from = sourceList.indexOf(activeId);
  const to = targetList.indexOf(overId);
  if (from === -1 || to === -1) {
    return null;
  }
  sourceList.splice(from, 1);
  targetList.splice(to, 0, activeId);
  next.set(activeKey, sourceList);
  next.set(overKey, targetList);

  const nextGroups = new Map(groups);
  nextGroups.set(activeId, groups.get(overId));

  return { orderByGroup: next, groups: nextGroups };
}
